import React, { HTMLAttributes, ReactNode } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'highlighted' | 'stat';
  children: ReactNode;
}

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  action?: ReactNode;
  children?: ReactNode;
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Card component following Notion design patterns
 * - Padding: 20px
 * - Border: 1px solid #E8E8E8
 * - Border radius: 12px
 * - Shadow: subtle elevation
 */
export const Card: React.FC<CardProps> = ({
  variant = 'default',
  children,
  className = '',
  ...props
}) => {
  const baseClasses =
    'bg-white rounded-lg border transition-all duration-200';

  const variantClasses = {
    default:
      'border-border-light shadow-sm ' +
      'hover:shadow-md hover:border-border',

    highlighted:
      'border-l-4 border-l-orange-400 border-t-border-light border-r-border-light border-b-border-light ' +
      'bg-orange-50 shadow-sm',

    stat:
      'border-border-light shadow-sm ' +
      'hover:shadow-md hover:border-border ' +
      'p-6',
  };

  const combinedClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${variant !== 'stat' ? 'p-5' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={combinedClasses} {...props}>
      {children}
    </div>
  );
};

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  action,
  children,
  className = '',
  ...props
}) => {
  return (
    <div
      className={`flex items-center justify-between pb-3 mb-4 border-b border-border-light ${className}`}
      {...props}
    >
      {children ? (
        children
      ) : (
        <>
          {title && (
            <h3 className="text-h4 font-semibold text-black">{title}</h3>
          )}
          {action && <div>{action}</div>}
        </>
      )}
    </div>
  );
};

export const CardBody: React.FC<CardBodyProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={`flex flex-col gap-3 ${className}`} {...props}>
      {children}
    </div>
  );
};

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div
      className={`flex justify-end gap-2 pt-4 mt-4 border-t border-border-light ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Stat Card - Specialized card for displaying metrics
 */
export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | number;
  change?: {
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  icon?: ReactNode;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  change,
  icon,
  className = '',
  ...props
}) => {
  const trendColors = {
    up: 'text-success-500',
    down: 'text-error-500',
    neutral: 'text-black-lighter',
  };

  const trendIcons = {
    up: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    ),
    down: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    ),
    neutral: null,
  };

  return (
    <Card variant="stat" className={className} {...props}>
      <div className="flex flex-col gap-2">
        {icon && (
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-orange-400 mb-2">
            {icon}
          </div>
        )}

        <div className="text-xs font-semibold text-black-lighter uppercase tracking-wide">
          {label}
        </div>

        <div className="text-h1 font-bold text-black leading-none">
          {value}
        </div>

        {change && (
          <div className={`flex items-center gap-1 text-xs font-semibold ${trendColors[change.trend]}`}>
            {trendIcons[change.trend]}
            <span>{change.value}</span>
          </div>
        )}
      </div>
    </Card>
  );
};
